/**
 * The usage log in the browser: a small queue and its transport.
 *
 * `track(name, detail)` puts one event of the vocabulary (`lib/usage-events.ts`) into a
 * queue in memory. The queue goes to `POST /api/usage` every few seconds, at once when
 * 20 events wait, and with `navigator.sendBeacon` when the page is hidden or closed.
 *
 * Rules that never bend:
 *   - Fail silent. No failure of this path shows to the person: no toast, no alert, no
 *     warning in the console. A lost event is only a lost count.
 *   - Nothing free is sent. `checkEvent` refuses every key and value outside the
 *     vocabulary, so no mail text, no name, no search word and no dialog text leaves.
 *   - Nothing of a practice case is sent, and practice clicks on a training page do not
 *     count as use of a feature.
 *   - Off without a `window` (the server render and every node test).
 *
 * The module does not use the generated SDK and adds no query or mutation, so its own
 * failures never show as a read or a write error of the app. No React here: the router
 * listener and the hooks are in `lib/usage-react.tsx`.
 */

import { NARROW_QUERY } from "@/hooks/use-media-query";
import { guardedFetch, PRACTICE_PREFIX } from "@/lib/api/practice-firewall";
import { logger } from "@/lib/logger";
import {
  checkEvent,
  USAGE_DURATION_MAX_MS,
  type UsageClient,
  type UsageDetail,
  usageEvent,
  type UsageEventName,
  usageIntMax,
  type UsageRoute,
} from "@/lib/usage-events";

export const USAGE_URL = "/api/usage";
/** `sessionStorage` key of the browser session id (one per tab, never a cookie). */
export const USAGE_SESSION_KEY = "i2q.usage.sid";
/**
 * A session id is a UUID (`crypto.randomUUID`) or 32 hex characters, in lower case. The
 * server takes no other shape, so the id can hold no word.
 */
export const USAGE_SESSION_RE =
  /^(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;

/** A flush on the timer: this long after the first event that waits. */
export const FLUSH_EVERY_MS = 10_000;
/** A flush at once when this many events wait. */
export const FLUSH_AT = 20;
/** Most events of one request (the server takes 50). */
export const BATCH_MAX = 50;
/** Most events that wait; the oldest is dropped. */
export const QUEUE_MAX = 200;
/**
 * Most events of one hour. The count starts again each hour, so a tab that stays open
 * for days keeps reporting (a count for the whole page life made the people who use
 * I2Q most go silent).
 */
export const SESSION_MAX = 2_000;
export const SESSION_MAX_WINDOW_MS = 60 * 60_000;
/** The wait after the server said "too many" (429). */
export const TOO_MANY_WAIT_MS = 60_000;
/** Failed flushes in a row before the pause. */
export const FAILS_BEFORE_PAUSE = 3;
export const FAIL_PAUSE_MS = 5 * 60_000;
/** Most script errors of one page life. */
export const SCRIPT_ERRORS_MAX = 5;
/** One read error per `what` in this time. */
export const READ_ERROR_EVERY_MS = 60_000;
/** Most requests of one flush on hide or close. */
const LEAVE_BATCHES_MAX = 4;

export interface TrackOptions {
  /** The case of the event. `null`: no case. Not given: the case of the page. */
  caseId?: string | null;
  durationMs?: number;
}

export type FlushHow = "timer" | "size" | "hidden" | "pagehide";

/** One event as `POST /usage` takes it (`UsageEventIn`). */
export interface UsageWireEvent {
  name: UsageEventName;
  ts: number;
  route: UsageRoute | null;
  case_id: string | null;
  duration_ms: number | null;
  detail: UsageDetail;
}

/** The body of `POST /usage` (`UsageBatch`). */
export interface UsageWireBatch {
  session_id: string;
  client: UsageClient;
  app_version: string;
  sent_at: number;
  events: UsageWireEvent[];
}

/** What the transport tells the queue: the HTTP status, and `enabled` of a 202. */
export interface UsageAnswer {
  status: number;
  enabled?: boolean;
}

/** Everything the queue needs from the outside, so a test can replace each part. */
export interface UsageDeps {
  now: () => number;
  /** The keepalive fetch. It may reject (no network). */
  send: (body: string) => Promise<UsageAnswer>;
  /** `navigator.sendBeacon`, or `null` where it is missing. False: not queued. */
  beacon: ((body: string) => boolean) | null;
  /** `sessionStorage`, or `null` when it is blocked. */
  storage: (Pick<Storage, "getItem" | "setItem"> & Partial<Pick<Storage, "removeItem">>) | null;
  /** A new random session id. */
  random: () => string;
  /** Whether the document is visible. */
  visible: () => boolean;
  client: () => UsageClient;
  appVersion: string;
  schedule: (run: () => void, ms: number) => unknown;
  cancel: (handle: unknown) => void;
  debug: (...args: unknown[]) => void;
}

export interface Usage {
  track: (name: UsageEventName, detail?: UsageDetail, options?: TrackOptions) => void;
  start: () => void;
  stop: () => void;
  setPage: (route: UsageRoute | null, caseId: string | null) => void;
  flush: (how: FlushHow) => void;
  sessionId: () => string;
  client: () => UsageClient;
  /** How many events wait (tests). */
  pending: () => number;
}

interface Held {
  events: UsageWireEvent[];
  /** Sends that failed with a network error or a 5xx. */
  tries: number;
}

function isPractice(caseId: string | null | undefined): boolean {
  return typeof caseId === "string" && caseId.startsWith(PRACTICE_PREFIX);
}

/** A whole number from 0 to `max`, or `null` for a value that is not a number. */
function wholeNumber(value: number, max: number): number | null {
  if (!Number.isFinite(value)) {
    return null;
  }
  return Math.min(max, Math.max(0, Math.round(value)));
}

/** Numbers as whole numbers inside their limit, so a fraction never refuses an event. */
function withWholeNumbers(detail: UsageDetail): UsageDetail {
  const out: UsageDetail = {};
  for (const [key, value] of Object.entries(detail)) {
    out[key] = typeof value === "number" ? wholeNumber(value, usageIntMax(key)) : value;
  }
  return out;
}

/** An id for a browser without `crypto`: 32 hex characters, random, from no other value. */
function fallbackSessionId(): string {
  let id = "";
  while (id.length < 32) {
    id += Math.floor(Math.random() * 16).toString(16);
  }
  return id;
}

/** The queue and its rules, with every outside part handed in. */
export function createUsage(deps: UsageDeps): Usage {
  let started = false;
  /** The server said the log is off: nothing more until the next full load. */
  let off = false;
  let queue: UsageWireEvent[] = [];
  let held: Held | null = null;
  let inFlight = false;
  let timer: unknown = null;
  let pausedUntil = 0;
  let failsInRow = 0;
  let tracked = 0;
  /** When the hour of `tracked` started. */
  let trackedSince = 0;
  let scriptErrors = 0;
  const lastReadError = new Map<string, number>();
  let page: { route: UsageRoute | null; caseId: string | null } = { route: null, caseId: null };
  let sid: string | null = null;

  function sessionId(): string {
    if (sid) {
      return sid;
    }
    try {
      const stored = deps.storage?.getItem(USAGE_SESSION_KEY);
      if (typeof stored === "string" && USAGE_SESSION_RE.test(stored)) {
        sid = stored;
        return sid;
      }
    } catch {
      // Storage is blocked: the id lives in memory.
    }
    let fresh = "";
    try {
      fresh = deps.random();
    } catch {
      // No random source: the fallback below.
    }
    sid = USAGE_SESSION_RE.test(fresh) ? fresh : fallbackSessionId();
    try {
      deps.storage?.setItem(USAGE_SESSION_KEY, sid);
    } catch {
      // Storage is blocked: the id lives in memory.
    }
    return sid;
  }

  /**
   * A stop or a sign-out ends the browser session: the next person on this tab gets an
   * id of their own, so two people never share one session id.
   */
  function forgetSession(): void {
    sid = null;
    try {
      deps.storage?.removeItem?.(USAGE_SESSION_KEY);
    } catch {
      // Storage is blocked: the id lived in memory only.
    }
  }

  function clearTimer(): void {
    if (timer !== null) {
      deps.cancel(timer);
      timer = null;
    }
  }

  function armTimer(): void {
    if (timer !== null || !started || off || (queue.length === 0 && !held)) {
      return;
    }
    const wait = Math.max(FLUSH_EVERY_MS, pausedUntil - deps.now());
    timer = deps.schedule(() => {
      timer = null;
      flush("timer");
    }, wait);
  }

  function failed(batch: Held, keep: boolean): void {
    if (keep && batch.tries === 0) {
      held = { events: batch.events, tries: 1 };
    }
    failsInRow += 1;
    if (failsInRow >= FAILS_BEFORE_PAUSE) {
      failsInRow = 0;
      pausedUntil = deps.now() + FAIL_PAUSE_MS;
    }
  }

  function answered(answer: UsageAnswer, batch: Held): void {
    const { status } = answer;
    if (status >= 200 && status < 300) {
      failsInRow = 0;
      if (answer.enabled === false) {
        off = true;
        queue = [];
        held = null;
        clearTimer();
        deps.debug("[usage]", "the log is off on the server");
      }
      return;
    }
    if (status === 401 || status === 403) {
      // Signed out or not allowed: nothing more until the next start.
      started = false;
      queue = [];
      held = null;
      clearTimer();
      forgetSession();
      return;
    }
    if (status === 429) {
      pausedUntil = deps.now() + TOO_MANY_WAIT_MS;
      return;
    }
    if (status === 413 || status === 422) {
      return;
    }
    failed(batch, status >= 500);
  }

  function nextBatch(): Held | null {
    if (held) {
      const batch = held;
      held = null;
      return batch;
    }
    if (queue.length === 0) {
      return null;
    }
    return { events: queue.splice(0, BATCH_MAX), tries: 0 };
  }

  function bodyOf(batch: Held): string {
    const body: UsageWireBatch = {
      session_id: sessionId(),
      client: deps.client(),
      app_version: deps.appVersion,
      sent_at: deps.now(),
      events: batch.events,
    };
    return JSON.stringify(body);
  }

  function sendWithFetch(batch: Held, body: string, after: () => void): void {
    let sent: Promise<UsageAnswer>;
    try {
      sent = deps.send(body);
    } catch (error) {
      deps.debug("[usage]", "send failed", error);
      failed(batch, true);
      after();
      return;
    }
    sent
      .then(
        (answer) => answered(answer, batch),
        (error: unknown) => {
          deps.debug("[usage]", "send failed", error);
          failed(batch, true);
        },
      )
      .then(after, after);
  }

  /** The page is hidden or closes: the beacon first, the keepalive fetch when it fails. */
  function flushLeaving(): void {
    for (let sends = 0; sends < LEAVE_BATCHES_MAX; sends += 1) {
      const batch = nextBatch();
      if (!batch) {
        return;
      }
      const body = bodyOf(batch);
      let queued = false;
      try {
        queued = deps.beacon ? deps.beacon(body) : false;
      } catch (error) {
        deps.debug("[usage]", "beacon failed", error);
      }
      if (!queued) {
        sendWithFetch(batch, body, () => undefined);
      }
    }
  }

  function flush(how: FlushHow): void {
    try {
      if (!started || off) {
        return;
      }
      if (deps.now() < pausedUntil) {
        armTimer();
        return;
      }
      if (how === "hidden" || how === "pagehide" || !deps.visible()) {
        clearTimer();
        flushLeaving();
        return;
      }
      if (inFlight) {
        return;
      }
      const batch = nextBatch();
      if (!batch) {
        return;
      }
      clearTimer();
      inFlight = true;
      sendWithFetch(batch, bodyOf(batch), () => {
        inFlight = false;
        if (queue.length >= FLUSH_AT && deps.now() >= pausedUntil) {
          flush("size");
        } else {
          armTimer();
        }
      });
    } catch (error) {
      inFlight = false;
      deps.debug("[usage]", "flush failed", error);
    }
  }

  /** The caps of two names: script errors, and one read error per `what` a minute. */
  function overCap(name: UsageEventName, detail: UsageDetail): boolean {
    if (name === "error.script") {
      scriptErrors += 1;
      return scriptErrors > SCRIPT_ERRORS_MAX;
    }
    if (name === "error.read") {
      const what = typeof detail.what === "string" ? detail.what : "";
      const last = lastReadError.get(what);
      const now = deps.now();
      if (last !== undefined && now - last < READ_ERROR_EVERY_MS) {
        return true;
      }
      lastReadError.set(what, now);
    }
    return false;
  }

  function track(name: UsageEventName, detail: UsageDetail = {}, options: TrackOptions = {}): void {
    try {
      if (!started || off) {
        return;
      }
      if (deps.now() - trackedSince >= SESSION_MAX_WINDOW_MS) {
        trackedSince = deps.now();
        tracked = 0;
      }
      if (tracked >= SESSION_MAX) {
        return;
      }
      if (isPractice(options.caseId) || isPractice(page.caseId)) {
        return;
      }
      // Practice clicks are not use of a feature. The print of the certificate is not a
      // practice click, so that one name passes.
      const onTraining = page.route?.startsWith("/training") ?? false;
      if (onTraining && !/^(page|perf|error)\./.test(name) && name !== "content.print") {
        return;
      }
      const event = usageEvent(name);
      const checked = checkEvent(name, withWholeNumbers(detail));
      if (!event || !checked) {
        deps.debug("[usage] refused", name);
        return;
      }
      if (overCap(name, checked)) {
        return;
      }
      const caseId = options.caseId === undefined ? page.caseId : options.caseId;
      const duration =
        event.duration && options.durationMs !== undefined
          ? wholeNumber(options.durationMs, USAGE_DURATION_MAX_MS)
          : null;
      queue.push({
        name,
        ts: deps.now(),
        route: page.route,
        case_id: event.case && caseId ? caseId : null,
        duration_ms: duration,
        detail: checked,
      });
      tracked += 1;
      if (queue.length > QUEUE_MAX) {
        queue.splice(0, queue.length - QUEUE_MAX);
      }
      if (queue.length >= FLUSH_AT) {
        flush("size");
      } else {
        armTimer();
      }
    } catch (error) {
      deps.debug("[usage]", "track failed", error);
    }
  }

  return {
    track,
    start: () => {
      if (started) {
        return;
      }
      started = true;
      try {
        sessionId();
      } catch (error) {
        deps.debug("[usage]", "no session id", error);
      }
      armTimer();
    },
    stop: () => {
      if (!started) {
        return;
      }
      flush("pagehide");
      clearTimer();
      started = false;
      forgetSession();
    },
    setPage: (route, caseId) => {
      page = { route, caseId: route === "/case/:id" ? caseId : null };
    },
    flush,
    sessionId,
    client: () => deps.client(),
    pending: () => queue.length + (held ? held.events.length : 0),
  };
}

/** The longest gap between two inputs that still counts as active time. */
export const ACTIVE_GAP_MAX_MS = 120_000;

/** The time on a page: the active time and the clock time of one visible stretch. */
export interface ActiveClock {
  /** A new stretch starts now. */
  start: () => void;
  /** The person did something (a key, a click, a scroll). */
  input: () => void;
  /** The times of the stretch up to now. */
  read: () => { activeMs: number; wallMs: number };
}

/**
 * Active time is the sum of the gaps between two inputs, each gap cut at 120 s. The
 * start and the end of a stretch count as an input. So a page left open over lunch adds
 * at most two minutes.
 */
export function createActiveClock(now: () => number): ActiveClock {
  let startedAt = now();
  let last = startedAt;
  let active = 0;
  const gap = (at: number) => Math.min(Math.max(0, at - last), ACTIVE_GAP_MAX_MS);
  return {
    start: () => {
      startedAt = now();
      last = startedAt;
      active = 0;
    },
    input: () => {
      const at = now();
      active += gap(at);
      last = at;
    },
    read: () => {
      const at = now();
      return { activeMs: active + gap(at), wallMs: Math.max(0, at - startedAt) };
    },
  };
}

// The browser parts. Everything below runs only where a `window` exists.

interface OfficeWindow {
  Office?: { context?: { mailbox?: unknown } };
}

function hasWindow(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/** The kind of device: the Outlook pane, a phone (a narrow window) or a desktop. */
function browserClient(): UsageClient {
  try {
    if ((window as unknown as OfficeWindow).Office?.context?.mailbox) {
      return "outlook_pane";
    }
    if (typeof window.matchMedia === "function" && window.matchMedia(NARROW_QUERY).matches) {
      return "phone";
    }
  } catch {
    // No answer: a desktop.
  }
  return "desktop";
}

/** A random id: `crypto.randomUUID()`, else 16 random bytes as hex. Never from a cookie. */
function randomSessionId(): string {
  const source = globalThis.crypto;
  if (typeof source?.randomUUID === "function") {
    return source.randomUUID();
  }
  const bytes = new Uint8Array(16);
  source.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function browserStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

async function sendWithKeepalive(body: string): Promise<UsageAnswer> {
  const response = await guardedFetch(USAGE_URL, {
    method: "POST",
    body,
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    keepalive: true,
  });
  if (response.status !== 202) {
    return { status: response.status };
  }
  try {
    const answer = (await response.json()) as { enabled?: unknown };
    return { status: 202, enabled: answer.enabled !== false };
  } catch {
    return { status: 202 };
  }
}

function sendWithBeacon(body: string): boolean {
  return navigator.sendBeacon(USAGE_URL, new Blob([body], { type: "application/json" }));
}

function appVersion(): string {
  const raw: unknown = import.meta.env.VITE_BUILD_SHA;
  return typeof raw === "string" && /^[A-Za-z0-9._-]{1,40}$/.test(raw) ? raw : "dev";
}

let browserUsage: Usage | null = null;
let removeListeners: (() => void) | null = null;

function instance(): Usage | null {
  if (!hasWindow()) {
    return null;
  }
  browserUsage ??= createUsage({
    now: () => Date.now(),
    send: sendWithKeepalive,
    beacon:
      typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
        ? sendWithBeacon
        : null,
    storage: browserStorage(),
    random: randomSessionId,
    visible: () => document.visibilityState !== "hidden",
    client: browserClient,
    appVersion: appVersion(),
    schedule: (run, ms) => window.setTimeout(run, ms),
    cancel: (handle) => window.clearTimeout(handle as number),
    debug: (...args) => logger.debug(...args),
  });
  return browserUsage;
}

/**
 * Record one event of the vocabulary. It never throws and never shows anything. It does
 * nothing without a `window`, before `startUsage()`, and after the server said the log
 * is off.
 */
export function track(name: UsageEventName, detail?: UsageDetail, options?: TrackOptions): void {
  try {
    instance()?.track(name, detail, options);
  } catch (error) {
    logger.debug("[usage]", "track failed", error);
  }
}

/** Start the queue, its timer and the two script-error listeners. Safe to call twice. */
export function startUsage(): void {
  const usage = instance();
  if (!usage) {
    return;
  }
  try {
    usage.start();
    if (!removeListeners) {
      const onError = () => track("error.script", { source: "error" }, { caseId: null });
      const onRejection = () => track("error.script", { source: "rejection" }, { caseId: null });
      window.addEventListener("error", onError);
      window.addEventListener("unhandledrejection", onRejection);
      removeListeners = () => {
        window.removeEventListener("error", onError);
        window.removeEventListener("unhandledrejection", onRejection);
      };
    }
  } catch (error) {
    logger.debug("[usage]", "start failed", error);
  }
}

/** Send what waits with the beacon, then remove the timer and the listeners. */
export function stopUsage(): void {
  try {
    browserUsage?.stop();
    removeListeners?.();
    removeListeners = null;
  } catch (error) {
    logger.debug("[usage]", "stop failed", error);
  }
}

/** The page of the events that follow: its route template and, on a case, its case id. */
export function setUsagePage(route: UsageRoute | null, caseId: string | null): void {
  instance()?.setPage(route, caseId);
}

/** Send what waits. `hidden` and `pagehide` use the beacon. Never throws. */
export function flushUsage(how: FlushHow): void {
  try {
    browserUsage?.flush(how);
  } catch (error) {
    logger.debug("[usage]", "flush failed", error);
  }
}

/** The random id of this tab. Empty without a `window`. */
export function usageSessionId(): string {
  return instance()?.sessionId() ?? "";
}

/** The kind of device of this tab. A desktop without a `window`. */
export function usageClient(): UsageClient {
  return hasWindow() ? browserClient() : "desktop";
}
